import React, { useState, useEffect } from 'react';
import { blogPosts, categories } from '../data/blogData';

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPost, setSelectedPost] = useState(null);
  const postsPerPage = 6;

  // Filter posts based on category and search term
  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const startIndex = (currentPage - 1) * postsPerPage;
  const endIndex = startIndex + postsPerPage;
  const currentPosts = filteredPosts.slice(startIndex, endIndex);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, searchTerm]);

  const handlePostClick = (post) => {
    setSelectedPost(post);
  };

  const closeModal = () => {
    setSelectedPost(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="blog-page">
      <section 
        className="hero-half"
        style={{
          backgroundImage: `url('/images/dvi-hero.webp')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="hero-content">
          <h1>Photography Blog</h1>
          <p>
            Stay updated with the latest photography tips, trends, and insights from Dvi Studios. 
            From technical tutorials to creative inspiration, our blog covers everything you need to know about photography.
          </p>
        </div>
      </section>
      
      <section className="blog-section">
        <div className="container">
          {/* Search and Filter Section */}
          <div className="blog-filters">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <span className="search-icon">🔍</span>
            </div>
            
            <div className="category-filters">
              {categories.map(category => (
                <button
                  key={category}
                  className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Results Summary */}
          <div className="results-summary">
            <p>
              Showing {currentPosts.length} of {filteredPosts.length} articles
              {selectedCategory !== 'All' && ` in ${selectedCategory}`}
              {searchTerm && ` for "${searchTerm}"`}
            </p>
          </div>

          {/* Blog Grid */}
          <div className="blog-grid">
            {currentPosts.map(post => (
              <div key={post.id} className="blog-card" onClick={() => handlePostClick(post)}>
                <div className="blog-image">
                  <img src={post.image} alt={post.title} />
                  {post.featured && <span className="featured-badge">Featured</span>}
                </div>
                <div className="blog-content">
                  <div className="blog-meta">
                    <span className="blog-category">{post.category}</span>
                    <span className="blog-date">{formatDate(post.date)}</span>
                    <span className="blog-read-time">{post.readTime}</span>
                  </div>
                  <h3 className="blog-title">{post.title}</h3>
                  <p className="blog-excerpt">{post.excerpt}</p>
                  <div className="blog-tags">
                    {post.tags.slice(0, 3).map(tag => (
                      <span key={tag} className="blog-tag">#{tag}</span>
                    ))}
                  </div>
                  <a href="#" className="read-more" onClick={(e) => {
                    e.preventDefault();
                    handlePostClick(post);
                  }}>Read More</a>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              
              <div className="pagination-numbers">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                ))}
              </div>
              
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Blog Post Modal */}
      {selectedPost && (
        <div className="blog-modal-overlay" onClick={closeModal}>
          <div className="blog-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="blog-modal-close" onClick={closeModal}>✕</button>
            <div className="blog-modal-header">
              <div className="blog-modal-meta">
                <span className="blog-modal-category">{selectedPost.category}</span>
                <span className="blog-modal-date">{formatDate(selectedPost.date)}</span>
                <span className="blog-modal-read-time">{selectedPost.readTime}</span>
              </div>
              <h1 className="blog-modal-title">{selectedPost.title}</h1>
              <p className="blog-modal-excerpt">{selectedPost.excerpt}</p>
            </div>
            <div className="blog-modal-image">
              <img src={selectedPost.image} alt={selectedPost.title} />
            </div>
            <div className="blog-modal-body" dangerouslySetInnerHTML={{ __html: selectedPost.content }} />
            <div className="blog-modal-footer">
              <div className="blog-modal-tags">
                {selectedPost.tags.map(tag => (
                  <span key={tag} className="blog-modal-tag">#{tag}</span>
                ))}
              </div>
              <div className="blog-modal-author">
                <span>By {selectedPost.author}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Blog;